import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getDiputadoById, getDiputadoStats, getGeneralStats, DiputadoPublic, DiputadoStats, DiputadosGeneralStats } from '../utils/api';
import { ArrowUp, ArrowDown, User, ArrowLeft, Calendar, Clock, XCircle, AlertCircle, CheckCircle, MapPin, Users, Flag, ChevronDown, ChevronUp } from 'lucide-react';
import { DateInput } from '../components/DateInput';
import { LoadingSpinner } from '../components/LoadingSpinner';
export function DiputadoDetail() {
  const [searchParams] = useSearchParams();
  const diputadoId = searchParams.get('id');
  const [diputado, setDiputado] = useState<DiputadoPublic | null>(null);
  const [diputadoStats, setDiputadoStats] = useState<DiputadoStats | null>(null);
  const [generalStats, setGeneralStats] = useState<DiputadosGeneralStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [startDate, setStartDate] = useState('2025-09-02');
  const [endDate, setEndDate] = useState('2025-12-02');
  const [isPeriodOpen, setIsPeriodOpen] = useState(false);
  const isDateRangeValid = startDate <= endDate;
  useEffect(() => {
    if (!diputadoId) {
      setError('ID de diputado no proporcionadou');
      setIsLoading(false);
      return;
    }
    async function loadData() {
      try {
        setIsLoading(true);
        setError(null);
        setImageLoaded(false);
        const diputadoData = await getDiputadoById(diputadoId!);
        setDiputado(diputadoData);
        if (diputadoData.image_path) {
          const img = new Image();
          img.onload = () => {
            setImageLoaded(true);
          };
          img.onerror = () => {
            setImageError(true);
            setImageLoaded(true);
          };
          img.src = diputadoData.image_path;
        } else {
          setImageLoaded(true);
        }
        if (isDateRangeValid) {
          try {
            const [stats, general] = await Promise.all([getDiputadoStats(diputadoId!, startDate, endDate), getGeneralStats(startDate, endDate)]);
            setDiputadoStats(stats);
            setGeneralStats(general);
          } catch (statsErr) {
            setDiputadoStats(null);
            setGeneralStats(null);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar datos');
        setIsLoading(false);
      }
    }
    loadData();
  }, [diputadoId]);
  useEffect(() => {
    if (imageLoaded && diputado) {
      setIsLoading(false);
    }
  }, [imageLoaded, diputado]);
  useEffect(() => {
    if (diputadoId && diputado && isDateRangeValid) {
      async function loadStats() {
        try {
          setIsLoadingStats(true);
          setError(null);
          const [stats, general] = await Promise.all([getDiputadoStats(diputadoId, startDate, endDate), getGeneralStats(startDate, endDate)]);
          setDiputadoStats(stats);
          setGeneralStats(general);
        } catch (err) {
          setDiputadoStats(null);
          setGeneralStats(null);
        } finally {
          setIsLoadingStats(false);
        }
      }
      loadStats();
    }
  }, [startDate, endDate, diputadoId, diputado, isDateRangeValid]);
  if (isLoading) {
    return <LoadingSpinner message="Cargando diputado..." data-id="element-352" />;
  }
  if (error || !diputado) {
    return <div className="w-full min-h-screen bg-white flex items-center justify-center px-8 py-24" data-id="element-353">
        <div className="bg-yellow border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-12 max-w-2xl" data-id="element-354">
          <h2 className="font-display text-3xl font-bold text-purple uppercase mb-4" data-id="element-355">
            Error
          </h2>
          <p className="text-lg text-[#1A1A1A] mb-6" data-id="element-356">
            {error || 'Diputado no encontrado'}
          </p>
          <Link to="/stats" className="inline-block bg-purple border-brutal border-yellow text-white font-display uppercase px-6 py-3 hover:bg-white hover:text-purple transition-colors duration-100" data-id="element-357">
            Volver a Estadísticas
          </Link>
        </div>
      </div>;
  }
  const asistencias = diputadoStats ? diputadoStats.total_sesiones - diputadoStats.total_inasistencias : 0;
  const promedioAsistencias = generalStats ? generalStats.total_sesiones - generalStats.promedio_inasistencias : 0;
  const porcentajeAsistencias = diputadoStats ? asistencias / diputadoStats.total_sesiones * 100 : 0;
  const hasNoSessions = !isLoadingStats && (!diputadoStats || diputadoStats.total_sesiones === 0);
  const SkeletonStatCard = () => <div className="bg-white border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-6" data-id="element-358">
      <div className="flex items-start justify-between mb-3" data-id="element-359">
        <div className="flex items-center gap-2" data-id="element-360">
          <div className="w-5 h-5 bg-lavender rounded animate-pulse" data-id="element-361"></div>
          <div className="h-3 w-24 bg-lavender rounded animate-pulse" data-id="element-362"></div>
        </div>
        <div className="w-5 h-5 bg-lavender rounded animate-pulse" data-id="element-363"></div>
      </div>
      <div className="h-9 w-32 bg-lavender rounded animate-pulse mb-1" data-id="element-364"></div>
      <div className="h-5 w-16 bg-lavender rounded animate-pulse mb-2" data-id="element-365"></div>
      <div className="h-4 w-40 bg-lavender rounded animate-pulse" data-id="element-366"></div>
    </div>;
  const PeriodoStatCard = ({
    label,
    value,
    unit,
    percentage,
    average,
    isGoodPerformance,
    showUpArrow,
    icon: Icon
  }: {
    label: string;
    value: number;
    unit: string;
    percentage: number;
    average: number;
    isGoodPerformance: boolean;
    showUpArrow: boolean;
    icon: React.ElementType;
  }) => <div className="bg-white border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-6 hover:shadow-brutal-hover transition-all duration-100" data-id="element-367">
      <div className="flex items-start justify-between mb-3" data-id="element-368">
        <div className="flex items-center gap-2" data-id="element-369">
          <Icon className="w-5 h-5 text-purple" data-id="element-370" />
          <h3 className="font-display text-xs uppercase tracking-wide text-[#1A1A1A] font-bold" data-id="element-371">
            {label}
          </h3>
        </div>
        {showUpArrow ? <ArrowUp className={`w-5 h-5 flex-shrink-0 ${isGoodPerformance ? 'text-green-600' : 'text-red-600'}`} data-id="element-372" /> : <ArrowDown className={`w-5 h-5 flex-shrink-0 ${isGoodPerformance ? 'text-green-600' : 'text-red-600'}`} data-id="element-373" />}
      </div>
      <div className="font-display text-3xl font-bold text-purple mb-1" data-id="element-374">
        {value}
        <span className="text-base text-[#1A1A1A] ml-1" data-id="element-375">{unit}</span>
      </div>
      <div className="text-sm text-purple font-bold mb-2" data-id="element-376">
        {percentage.toFixed(1)}%
      </div>
      <div className="text-xs text-[#1A1A1A]" data-id="element-377">
        Promedio: <span className="font-bold" data-id="element-378">{average.toFixed(1)}</span> {unit}
      </div>
    </div>;
  // Mobile compact stat card - with ARROWS instead of dots
  const MobileStatCard = ({
    label,
    value,
    unit,
    isGoodPerformance,
    showUpArrow,
    icon: Icon
  }: {
    label: string;
    value: number;
    unit: string;
    isGoodPerformance: boolean;
    showUpArrow: boolean;
    icon: React.ElementType;
  }) => <div className="flex items-center justify-between py-3 border-b-2 border-[#1A1A1A]/20 last:border-b-0" data-id="element-379">
      <div className="flex items-center gap-2 flex-1" data-id="element-380">
        <Icon className="w-5 h-5 text-purple flex-shrink-0" data-id="element-381" />
        <span className="font-display text-xs uppercase tracking-wide text-[#1A1A1A] font-bold" data-id="element-382">
          {label}
        </span>
      </div>
      <div className="flex items-center gap-2" data-id="element-383">
        {showUpArrow ? <ArrowUp className={`w-4 h-4 flex-shrink-0 ${isGoodPerformance ? 'text-green-600' : 'text-red-600'}`} data-id="element-384" /> : <ArrowDown className={`w-4 h-4 flex-shrink-0 ${isGoodPerformance ? 'text-green-600' : 'text-red-600'}`} data-id="element-385" />}
        <span className="font-display text-xl font-bold text-purple whitespace-nowrap" data-id="element-386">
          {value}
          <span className="text-sm text-[#1A1A1A] ml-1" data-id="element-387">{unit}</span>
        </span>
      </div>
    </div>;
  return <div className="w-full min-h-screen bg-white" data-id="element-388">
      <div className="max-w-7xl mx-auto" data-id="element-389">
        {/* Mobile Compact View - Full Screen with Volver visible */}
        <div className="md:hidden h-screen flex flex-col" data-id="element-390">
          {/* Main Card - Takes full height */}
          <div className="flex-1 flex flex-col p-3 overflow-hidden" data-id="element-391">
            <div className="bg-yellow border-brutal border-[#1A1A1A] rounded-none shadow-brutal flex-1 flex flex-col min-h-0" data-id="element-392">
              {/* Header Section - Compact */}
              <div className="p-4 flex-shrink-0" data-id="element-393">
                <div className="flex gap-3 mb-3" data-id="element-394">
                  {/* Image */}
                  <div className="w-24 h-24 bg-lavender border-brutal border-[#1A1A1A] rounded-none flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-395">
                    {diputado.image_path && !imageError ? <img src={diputado.image_path} alt={diputado.nombre} className="w-full h-full object-cover" onError={() => setImageError(true)} data-id="element-396" /> : <User className="w-12 h-12 text-purple" data-id="element-397" />}
                  </div>

                  {/* Name */}
                  <div className="flex-1 min-w-0 flex items-center" data-id="element-398">
                    <h1 className="font-display text-xl font-bold text-purple uppercase tracking-tight leading-tight" data-id="element-399">
                      {diputado.nombre}
                    </h1>
                  </div>
                </div>

                {/* Info row: Distrito, Partido, and Bancada */}
                <div className="flex flex-wrap gap-2" data-id="element-400">
                  <div className="flex items-center gap-1 bg-white border-2 border-[#1A1A1A] rounded-none px-2 py-1" data-id="element-401">
                    <MapPin className="w-3 h-3 text-purple flex-shrink-0" data-id="element-402" />
                    <span className="font-display text-xs font-bold text-purple" data-id="element-403">
                      D{diputado.distrito_numero}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 bg-white border-2 border-[#1A1A1A] rounded-none px-2 py-1" data-id="element-404">
                    <Flag className="w-3 h-3 text-purple flex-shrink-0" data-id="element-405" />
                    <span className="font-display text-xs font-bold text-purple uppercase" data-id="element-406">
                      {diputado.partido}
                    </span>
                  </div>
                  {diputado.bancada.length > 0 && <div className="flex items-center gap-1 bg-white border-2 border-[#1A1A1A] rounded-none px-2 py-1" data-id="element-407">
                      <Users className="w-3 h-3 text-purple flex-shrink-0" data-id="element-408" />
                      <span className="font-display text-xs font-bold text-purple uppercase" data-id="element-409">
                        {diputado.bancada[0]}
                      </span>
                    </div>}
                </div>
              </div>

              {/* Period Selector - Accordion */}
              <div className="border-t-brutal border-[#1A1A1A] bg-white flex-shrink-0" data-id="element-410">
                <button onClick={() => setIsPeriodOpen(!isPeriodOpen)} className="w-full flex items-center justify-between p-3 hover:bg-lavender/20 transition-colors duration-100" data-id="element-411">
                  <div className="flex items-center gap-2" data-id="element-412">
                    <Calendar className="w-4 h-4 text-purple" data-id="element-413" />
                    <span className="font-display text-xs uppercase tracking-wide text-purple font-bold" data-id="element-414">
                      {startDate} - {endDate}
                    </span>
                  </div>
                  {isPeriodOpen ? <ChevronUp className="w-4 h-4 text-purple" data-id="element-415" /> : <ChevronDown className="w-4 h-4 text-purple" data-id="element-416" />}
                </button>

                {isPeriodOpen && <div className="p-3 border-t-2 border-[#1A1A1A]/20" data-id="element-417">
                    <div className="space-y-2" data-id="element-418">
                      <DateInput label="Fecha Inicio" value={startDate} onChange={setStartDate} error={!isDateRangeValid} data-id="element-419" />
                      <DateInput label="Fecha Fin" value={endDate} onChange={setEndDate} error={!isDateRangeValid} data-id="element-420" />
                    </div>
                    {!isDateRangeValid && <div className="mt-2 flex items-center gap-2 text-red-600" data-id="element-421">
                        <AlertCircle className="w-3 h-3 flex-shrink-0" data-id="element-422" />
                        <span className="font-display text-xs uppercase tracking-wide font-bold" data-id="element-423">
                          Fecha fin debe ser posterior
                        </span>
                      </div>}
                  </div>}
              </div>

              {/* Stats Section - Scrollable */}
              {isDateRangeValid && <div className="flex-1 border-t-brutal border-[#1A1A1A] bg-white p-4 overflow-y-auto min-h-0" data-id="element-424">
                  {isLoadingStats ? <div className="space-y-2" data-id="element-425">
                      {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-10 bg-lavender rounded animate-pulse" data-id="element-426"></div>)}
                    </div> : hasNoSessions ? <div className="text-center py-6" data-id="element-427">
                      <AlertCircle className="w-10 h-10 text-purple mx-auto mb-2" data-id="element-428" />
                      <p className="font-display text-xs uppercase text-[#1A1A1A] font-bold" data-id="element-429">
                        Sin datos en este período
                      </p>
                    </div> : diputadoStats && generalStats ? <div className="space-y-1" data-id="element-430">
                      <MobileStatCard label="Asistencias" value={asistencias} unit="sesiones" isGoodPerformance={asistencias >= promedioAsistencias} showUpArrow={asistencias >= promedioAsistencias} icon={CheckCircle} data-id="element-431" />
                      <MobileStatCard label="Tiempo Efectivo" value={Math.round(diputadoStats.sesiones_mins_efectivo / 60)} unit="hrs" isGoodPerformance={diputadoStats.sesiones_mins_efectivo >= generalStats.promedio_sesiones_mins_efectivo} showUpArrow={diputadoStats.sesiones_mins_efectivo >= generalStats.promedio_sesiones_mins_efectivo} icon={Clock} data-id="element-432" />
                      <MobileStatCard label="Inasistencias" value={diputadoStats.total_inasistencias} unit="sesiones" isGoodPerformance={diputadoStats.total_inasistencias <= generalStats.promedio_inasistencias} showUpArrow={diputadoStats.total_inasistencias >= generalStats.promedio_inasistencias} icon={XCircle} data-id="element-433" />
                      <MobileStatCard label="Inasist. Injust." value={diputadoStats.inasistencias_injustificadas} unit="sesiones" isGoodPerformance={diputadoStats.inasistencias_injustificadas <= generalStats.promedio_inasistencias_injustificadas} showUpArrow={diputadoStats.inasistencias_injustificadas >= generalStats.promedio_inasistencias_injustificadas} icon={AlertCircle} data-id="element-434" />
                      <MobileStatCard label="Atrasos" value={diputadoStats.atrasos} unit="sesiones" isGoodPerformance={diputadoStats.atrasos <= generalStats.promedio_atrasos} showUpArrow={diputadoStats.atrasos >= generalStats.promedio_atrasos} icon={Clock} data-id="element-435" />
                    </div> : null}
                </div>}
            </div>
          </div>

          {/* Volver button - Fixed at bottom */}
          <div className="flex-shrink-0 p-3 pt-0" data-id="element-436">
            <Link to="/stats" className="flex items-center justify-center gap-2 w-full bg-purple border-brutal border-yellow text-white font-display uppercase text-sm font-bold px-4 py-3 hover:bg-white hover:text-purple transition-colors duration-100" data-id="element-437">
              <ArrowLeft className="w-4 h-4" data-id="element-438" />
              <span data-id="element-439">Volver</span>
            </Link>
          </div>
        </div>

        {/* Desktop View - Original Layout */}
        <div className="hidden md:block px-8 py-16" data-id="element-440">
          <Link to="/stats" className="inline-flex items-center gap-2 mb-8 text-purple hover:text-yellow transition-colors duration-100" data-id="element-441">
            <ArrowLeft className="w-5 h-5" data-id="element-442" />
            <span className="font-display uppercase tracking-wide text-sm font-bold" data-id="element-443">
              Volver
            </span>
          </Link>

          {/* Hero Card - Redesigned */}
          <div className="bg-yellow border-brutal border-[#1A1A1A] rounded-none shadow-brutal mb-8" data-id="element-444">
            <div className="p-8" data-id="element-445">
              <div className="flex flex-col md:flex-row gap-8" data-id="element-446">
                {/* Image */}
                <div className="w-40 h-40 bg-lavender border-brutal border-[#1A1A1A] rounded-none flex items-center justify-center flex-shrink-0 overflow-hidden shadow-brutal" data-id="element-447">
                  {diputado.image_path && !imageError ? <img src={diputado.image_path} alt={diputado.nombre} className="w-full h-full object-cover" onError={() => setImageError(true)} data-id="element-448" /> : <User className="w-20 h-20 text-purple" data-id="element-449" />}
                </div>

                {/* Info */}
                <div className="flex-1 flex flex-col justify-center" data-id="element-450">
                  <h1 className="font-display text-4xl lg:text-5xl font-bold text-purple uppercase tracking-tight leading-tight mb-6" data-id="element-451">
                    {diputado.nombre}
                  </h1>

                  <div className="flex flex-wrap gap-4" data-id="element-452">
                    {/* Partido */}
                    <div className="flex items-center gap-2 bg-white border-2 border-[#1A1A1A] rounded-none px-4 py-2" data-id="element-453">
                      <Flag className="w-5 h-5 text-purple flex-shrink-0" data-id="element-454" />
                      <div data-id="element-455">
                        <div className="font-display text-xs uppercase tracking-wide text-[#1A1A1A]" data-id="element-456">
                          Partido
                        </div>
                        <div className="font-display text-base font-bold text-purple uppercase" data-id="element-457">
                          {diputado.partido}
                        </div>
                      </div>
                    </div>

                    {/* Distrito */}
                    <div className="flex items-center gap-2 bg-white border-2 border-[#1A1A1A] rounded-none px-4 py-2" data-id="element-458">
                      <MapPin className="w-5 h-5 text-purple flex-shrink-0" data-id="element-459" />
                      <div data-id="element-460">
                        <div className="font-display text-xs uppercase tracking-wide text-[#1A1A1A]" data-id="element-461">
                          Distrito
                        </div>
                        <div className="font-display text-base font-bold text-purple" data-id="element-462">
                          Nº {diputado.distrito_numero}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bancada Section - Full Width Bottom */}
            <div className="border-t-brutal border-[#1A1A1A] bg-white p-6" data-id="element-463">
              <div className="flex items-start gap-3" data-id="element-464">
                <Users className="w-5 h-5 text-purple flex-shrink-0 mt-1" data-id="element-465" />
                <div className="flex-1" data-id="element-466">
                  <div className="font-display text-xs uppercase tracking-wide text-[#1A1A1A] mb-3 font-bold" data-id="element-467">
                    Bancada
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-468">
                    {diputado.bancada.map((bancada, index) => <span key={index} className="inline-block bg-yellow border-2 border-[#1A1A1A] rounded-none px-3 py-1 font-display text-sm font-bold text-purple uppercase" data-id="element-469">
                        {bancada}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-6 mb-6" data-id="element-470">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-center" data-id="element-471">
              <div className="lg:col-span-4" data-id="element-472">
                <div className="flex items-center gap-2 mb-4" data-id="element-473">
                  <Calendar className="w-5 h-5 text-purple" data-id="element-474" />
                  <h2 className="font-display text-sm uppercase tracking-wide text-purple font-bold" data-id="element-475">
                    Seleccionar Período
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-476">
                  <DateInput label="Fecha Inicio" value={startDate} onChange={setStartDate} error={!isDateRangeValid} data-id="element-477" />
                  <DateInput label="Fecha Fin" value={endDate} onChange={setEndDate} error={!isDateRangeValid} data-id="element-478" />
                </div>
                {!isDateRangeValid && <div className="mt-3 flex items-center gap-2 text-red-600" data-id="element-479">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-480" />
                    <span className="font-display text-xs uppercase tracking-wide font-bold" data-id="element-481">
                      La fecha de fin debe ser posterior a la fecha de inicio
                    </span>
                  </div>}
              </div>

              <div className="bg-yellow border-brutal border-[#1A1A1A] rounded-none p-6 flex flex-col justify-center items-center text-center" data-id="element-482">
                <div className="font-display text-xs uppercase tracking-wide text-[#1A1A1A] mb-2 font-bold" data-id="element-483">
                  Total Sesiones en ese periodo
                </div>
                {isLoadingStats ? <div className="h-8 w-16 bg-lavender rounded animate-pulse" data-id="element-484"></div> : <div className="font-display text-2xl font-bold text-purple" data-id="element-485">
                    {diputadoStats?.total_sesiones ?? '0'}
                  </div>}
              </div>
            </div>
          </div>

          {isDateRangeValid ? isLoadingStats ? <div className="space-y-4" data-id="element-486">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-487">
                  <SkeletonStatCard data-id="element-488" />
                  <SkeletonStatCard data-id="element-489" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-490">
                  <SkeletonStatCard data-id="element-491" />
                  <SkeletonStatCard data-id="element-492" />
                  <SkeletonStatCard data-id="element-493" />
                </div>
              </div> : hasNoSessions ? <div className="bg-yellow border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-12 text-center" data-id="element-494">
                <AlertCircle className="w-16 h-16 text-purple mx-auto mb-4" data-id="element-495" />
                <h3 className="font-display text-2xl font-bold text-purple uppercase mb-2" data-id="element-496">
                  Sin Datos
                </h3>
                <p className="text-lg text-[#1A1A1A]" data-id="element-497">
                  No se encontraron sesiones en el período seleccionado
                </p>
              </div> : diputadoStats && generalStats ? <div className="space-y-4" data-id="element-498">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-499">
                  <PeriodoStatCard label="Asistencias" value={asistencias} unit="sesiones" percentage={porcentajeAsistencias} average={promedioAsistencias} isGoodPerformance={asistencias >= promedioAsistencias} showUpArrow={asistencias >= promedioAsistencias} icon={CheckCircle} data-id="element-500" />
                  <PeriodoStatCard label="Tiempo Efectivo" value={Math.round(diputadoStats.sesiones_mins_efectivo / 60)} unit="horas" percentage={diputadoStats.porcentaje_sesiones_mins_efectivo} average={generalStats.promedio_sesiones_mins_efectivo / 60} isGoodPerformance={diputadoStats.sesiones_mins_efectivo >= generalStats.promedio_sesiones_mins_efectivo} showUpArrow={diputadoStats.sesiones_mins_efectivo >= generalStats.promedio_sesiones_mins_efectivo} icon={Clock} data-id="element-501" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-502">
                  <PeriodoStatCard label="Inasistencias" value={diputadoStats.total_inasistencias} unit="sesiones" percentage={diputadoStats.porcentaje_inasistencias} average={generalStats.promedio_inasistencias} isGoodPerformance={diputadoStats.total_inasistencias <= generalStats.promedio_inasistencias} showUpArrow={diputadoStats.total_inasistencias >= generalStats.promedio_inasistencias} icon={XCircle} data-id="element-503" />
                  <PeriodoStatCard label="Inasist. Injustificadas" value={diputadoStats.inasistencias_injustificadas} unit="sesiones" percentage={diputadoStats.porcentaje_inasistencias_injustificadas} average={generalStats.promedio_inasistencias_injustificadas} isGoodPerformance={diputadoStats.inasistencias_injustificadas <= generalStats.promedio_inasistencias_injustificadas} showUpArrow={diputadoStats.inasistencias_injustificadas >= generalStats.promedio_inasistencias_injustificadas} icon={AlertCircle} data-id="element-504" />
                  <PeriodoStatCard label="Atrasos" value={diputadoStats.atrasos} unit="sesiones" percentage={diputadoStats.porcentaje_atrasos} average={generalStats.promedio_atrasos} isGoodPerformance={diputadoStats.atrasos <= generalStats.promedio_atrasos} showUpArrow={diputadoStats.atrasos >= generalStats.promedio_atrasos} icon={Clock} data-id="element-505" />
                </div>
              </div> : null : <div className="bg-yellow border-brutal border-[#1A1A1A] rounded-none shadow-brutal p-12 text-center" data-id="element-506">
              <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" data-id="element-507" />
              <h3 className="font-display text-2xl font-bold text-purple uppercase mb-2" data-id="element-508">
                Rango de Fechas Inválido
              </h3>
              <p className="text-lg text-[#1A1A1A]" data-id="element-509">
                Por favor, selecciona un rango de fechas válido para ver las
                estadísticas.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}